/**
 * Charge Existing Customer Dialog
 *
 * Admin-only flow to charge an agent's saved Stripe payment method directly,
 * without sending a new Stripe Checkout link. Intended for rare, manual
 * situations (e.g. rolling undelivered leads into a follow-up package for a
 * customer who has explicitly authorized the additional charge).
 *
 * Guardrails:
 *   - Admin-only (enforced by the Edge Function).
 *   - Explicit authorization checkbox + type-to-confirm input.
 *   - Two-step UI: configure → review → confirm charge.
 *   - Handles paid / auth_required (3DS) / charge_failed responses.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  CreditCard,
  Loader2,
  Pencil,
  ShieldAlert,
  UserCheck,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const LEAD_TYPES_BY_VERTICAL: Record<string, Array<{ id: string; name: string }>> = {
  life_insurance: [
    { id: "life_fex", name: "Final Expense (FEX)" },
    { id: "life_iul", name: "Indexed Universal Life (IUL)" },
    { id: "life_mp", name: "Mortgage Protection (MP)" },
  ],
  mortgage: [
    { id: "mortgage_purchase", name: "Purchase" },
    { id: "mortgage_refi", name: "Refinance" },
    { id: "mortgage_heloc", name: "HELOC" },
    { id: "mortgage_reverse", name: "Reverse Mortgage" },
  ],
};

interface ChargeExistingCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ChargeResult =
  | { status: "paid"; proposalId: string; invoiceId: string; hostedInvoiceUrl: string | null }
  | { status: "pending"; proposalId: string; invoiceId: string; hostedInvoiceUrl: string | null }
  | { status: "auth_required"; proposalId: string; invoiceId: string; hostedInvoiceUrl: string | null }
  | { status: "charge_failed"; proposalId: string; invoiceId: string; error: string };

interface AgentOption {
  email: string;
  name: string;
  stripeCustomerId: string;
  activeContracts: number;
  mostRecentContractStatus: string | null;
}

export function ChargeExistingCustomerDialog({
  open,
  onOpenChange,
}: ChargeExistingCustomerDialogProps) {
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const [vertical, setVertical] = useState("life_insurance");
  const [leadTypes, setLeadTypes] = useState<string[]>(["life_iul"]);
  const [totalLeads, setTotalLeads] = useState(40);
  const [pricePerLead, setPricePerLead] = useState(55);
  const [cycleDays, setCycleDays] = useState(30);
  const [notes, setNotes] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const [step, setStep] = useState<"configure" | "review">("configure");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ChargeResult | null>(null);

  const totalAmount = useMemo(() => totalLeads * pricePerLead, [totalLeads, pricePerLead]);
  const totalLabel = useMemo(() => `$${totalAmount.toFixed(2)}`, [totalAmount]);

  // Load eligible agents (those with at least one contract that has a Stripe
  // customer) when the dialog opens. We dedupe by email and keep the most
  // recent contract's status for display. This list drives the picker UI so
  // admins never have to retype the agent's email or name.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      setLoadingAgents(true);
      const { data, error } = await supabase
        .from("contracts")
        .select("agent_name, agent_email, stripe_customer_id, status, created_at")
        .not("stripe_customer_id", "is", null)
        .not("agent_email", "is", null)
        .order("created_at", { ascending: false })
        .limit(500);

      if (cancelled) return;

      if (error) {
        console.error("[ChargeExistingCustomerDialog] Failed to load agents:", error);
        toast.error("Failed to load agent list.");
        setLoadingAgents(false);
        return;
      }

      const byEmail = new Map<string, AgentOption>();
      for (const row of data ?? []) {
        const email = (row.agent_email as string).toLowerCase();
        const existing = byEmail.get(email);
        const isActive = row.status === "active" || row.status === "at_risk";
        if (!existing) {
          byEmail.set(email, {
            email: row.agent_email as string,
            name: (row.agent_name as string) || email,
            stripeCustomerId: row.stripe_customer_id as string,
            activeContracts: isActive ? 1 : 0,
            mostRecentContractStatus: (row.status as string) ?? null,
          });
        } else if (isActive) {
          existing.activeContracts += 1;
        }
      }

      setAgents(
        Array.from(byEmail.values()).sort((a, b) =>
          b.activeContracts - a.activeContracts || a.name.localeCompare(b.name),
        ),
      );
      setLoadingAgents(false);
    })();

    return () => { cancelled = true; };
  }, [open]);

  const reset = () => {
    setSelectedAgent(null);
    setVertical("life_insurance");
    setLeadTypes(["life_iul"]);
    setTotalLeads(40);
    setPricePerLead(55);
    setCycleDays(30);
    setNotes("");
    setAuthorized(false);
    setConfirmText("");
    setStep("configure");
    setResult(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onOpenChange(false);
  };

  const handleLeadTypeToggle = (id: string, checked: boolean) => {
    setLeadTypes((prev) => (checked ? [...prev, id] : prev.filter((t) => t !== id)));
  };

  const canProceedToReview = useMemo(() => {
    if (!selectedAgent) return false;
    if (leadTypes.length === 0) return false;
    if (!Number.isInteger(totalLeads) || totalLeads < 1) return false;
    if (pricePerLead < 0.5) return false;
    if (!Number.isInteger(cycleDays) || cycleDays < 1) return false;
    return true;
  }, [selectedAgent, leadTypes, totalLeads, pricePerLead, cycleDays]);

  const canConfirm = authorized && confirmText.trim().toUpperCase() === "CHARGE";

  const handleCharge = async () => {
    if (!canConfirm || !selectedAgent) return;
    setLoading(true);
    setResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Authentication required. Please sign in again.");
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("charge-existing-customer", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          agentEmail: selectedAgent.email,
          agentName: selectedAgent.name,
          vertical,
          leadTypes,
          totalLeads,
          pricePerLead,
          cycleDays,
          notes: notes.trim() || undefined,
          confirmationToken: "I_AUTHORIZE_THIS_CHARGE",
        },
      });

      let errorBody: Record<string, unknown> | null = null;
      if (error && typeof error === "object" && "context" in error) {
        try {
          errorBody = await (error as unknown as { context: Response }).context.json();
        } catch {
          /* ignore */
        }
      }

      if (error && !data) {
        const detail = (errorBody?.detail ?? errorBody?.error) as string | undefined;
        toast.error(detail || "Failed to submit charge.");
        setLoading(false);
        return;
      }

      const status = (data?.status as string | undefined) ?? null;

      if (status === "paid") {
        setResult({
          status: "paid",
          proposalId: data.proposalId,
          invoiceId: data.invoiceId,
          hostedInvoiceUrl: data.hostedInvoiceUrl ?? null,
        });
        toast.success(`Charged ${totalLabel}. Contract is being created.`);
      } else if (status === "pending") {
        setResult({
          status: "pending",
          proposalId: data.proposalId,
          invoiceId: data.invoiceId,
          hostedInvoiceUrl: data.hostedInvoiceUrl ?? null,
        });
        toast.info("Charge submitted. Waiting for Stripe confirmation.");
      } else if (status === "auth_required") {
        setResult({
          status: "auth_required",
          proposalId: data.proposalId,
          invoiceId: data.invoiceId,
          hostedInvoiceUrl: data.hostedInvoiceUrl ?? null,
        });
        toast.warning("Customer authentication (3DS) required. Share the hosted URL with the agent.");
      } else {
        setResult({
          status: "charge_failed",
          proposalId: data?.proposalId,
          invoiceId: data?.invoiceId,
          error: (data?.error as string) || "Charge failed",
        });
        toast.error((data?.error as string) || "Charge failed");
      }
    } catch (err) {
      console.error("[ChargeExistingCustomerDialog] Error:", err);
      toast.error("Unexpected error while processing charge.");
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <CreditCard className="h-5 w-5 text-accent" />
            Charge Existing Customer
          </DialogTitle>
          <DialogDescription className="text-sm">
            Debit the agent's saved card for a new prepaid package — no payment link required.
            Use only when the agent has explicitly authorized the additional charge.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 overflow-y-auto flex-1 px-6 py-5">
          {result ? (
            <ResultPanel result={result} totalLabel={totalLabel} onCopy={copyText} />
          ) : step === "configure" ? (
            <ConfigurePanel
              selectedAgent={selectedAgent}
              setSelectedAgent={setSelectedAgent}
              agents={agents}
              loadingAgents={loadingAgents}
              vertical={vertical}
              setVertical={(v) => {
                setVertical(v);
                const first = LEAD_TYPES_BY_VERTICAL[v]?.[0]?.id;
                setLeadTypes(first ? [first] : []);
              }}
              leadTypes={leadTypes}
              onLeadTypeToggle={handleLeadTypeToggle}
              totalLeads={totalLeads}
              setTotalLeads={setTotalLeads}
              pricePerLead={pricePerLead}
              setPricePerLead={setPricePerLead}
              cycleDays={cycleDays}
              setCycleDays={setCycleDays}
              notes={notes}
              setNotes={setNotes}
              totalLabel={totalLabel}
            />
          ) : selectedAgent ? (
            <ReviewPanel
              selectedAgent={selectedAgent}
              totalLeads={totalLeads}
              pricePerLead={pricePerLead}
              cycleDays={cycleDays}
              totalLabel={totalLabel}
              leadTypes={leadTypes}
              vertical={vertical}
              authorized={authorized}
              setAuthorized={setAuthorized}
              confirmText={confirmText}
              setConfirmText={setConfirmText}
            />
          ) : null}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 px-6 py-4 border-t bg-muted/20">
          {result ? (
            <Button onClick={handleClose} className="w-full sm:w-auto sm:ml-auto">Close</Button>
          ) : step === "configure" ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={loading}>Cancel</Button>
              <Button
                disabled={!canProceedToReview}
                onClick={() => setStep("review")}
              >
                Review charge
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep("configure")} disabled={loading}>
                Back
              </Button>
              <Button
                disabled={!canConfirm || loading}
                onClick={handleCharge}
                className="bg-destructive hover:bg-destructive/90"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                Charge {totalLabel}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface ConfigurePanelProps {
  selectedAgent: AgentOption | null;
  setSelectedAgent: (agent: AgentOption | null) => void;
  agents: AgentOption[];
  loadingAgents: boolean;
  vertical: string;
  setVertical: (v: string) => void;
  leadTypes: string[];
  onLeadTypeToggle: (id: string, checked: boolean) => void;
  totalLeads: number;
  setTotalLeads: (v: number) => void;
  pricePerLead: number;
  setPricePerLead: (v: number) => void;
  cycleDays: number;
  setCycleDays: (v: number) => void;
  notes: string;
  setNotes: (v: string) => void;
  totalLabel: string;
}

function ConfigurePanel(props: ConfigurePanelProps) {
  const hasAgent = !!props.selectedAgent;

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          The agent's saved card will be charged immediately. This is an off-session
          (merchant-initiated) transaction — only use with prior authorization from the client.
        </div>
      </div>

      <SectionHeader
        title="Agent"
        hint="Only agents with a contract that has a saved Stripe payment method appear in the list."
      />
      {hasAgent ? (
        <SelectedAgentCard
          agent={props.selectedAgent!}
          onChange={() => props.setSelectedAgent(null)}
        />
      ) : (
        <AgentPicker
          agents={props.agents}
          loading={props.loadingAgents}
          onSelect={props.setSelectedAgent}
        />
      )}

      {hasAgent && (
        <>
          <Separator />

          <SectionHeader title="Lead configuration" />
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
            <FieldGroup label="Vertical">
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={props.vertical}
                onChange={(e) => props.setVertical(e.target.value)}
              >
                <option value="life_insurance">Life Insurance</option>
                <option value="mortgage">Mortgage</option>
              </select>
            </FieldGroup>
            <FieldGroup label="Lead types">
              <div className="rounded-md border divide-y">
                {(LEAD_TYPES_BY_VERTICAL[props.vertical] || []).map((lt) => (
                  <div key={lt.id} className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm">{lt.name}</span>
                    <Switch
                      checked={props.leadTypes.includes(lt.id)}
                      onCheckedChange={(checked) => props.onLeadTypeToggle(lt.id, checked)}
                    />
                  </div>
                ))}
              </div>
            </FieldGroup>
          </div>

          <Separator />

          <SectionHeader title="Pricing" />
          <div className="grid grid-cols-3 gap-4">
            <FieldGroup label="Leads">
              <Input
                type="number"
                min={1}
                value={props.totalLeads}
                onChange={(e) => props.setTotalLeads(Number(e.target.value))}
              />
            </FieldGroup>
            <FieldGroup label="Price / lead ($)">
              <Input
                type="number"
                min={0.5}
                step={0.5}
                value={props.pricePerLead}
                onChange={(e) => props.setPricePerLead(Number(e.target.value))}
              />
            </FieldGroup>
            <FieldGroup label="Duration (days)">
              <Input
                type="number"
                min={1}
                value={props.cycleDays}
                onChange={(e) => props.setCycleDays(Number(e.target.value))}
              />
            </FieldGroup>
          </div>

          <div className="rounded-md border bg-muted/30 px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total charge</span>
            <span className="text-lg font-semibold tabular-nums">{props.totalLabel}</span>
          </div>

          <Separator />

          <FieldGroup label="Internal notes">
            <Textarea
              placeholder="e.g. Rolling undelivered leads from previous cycle into new package — agent confirmed via email on ..."
              value={props.notes}
              onChange={(e) => props.setNotes(e.target.value)}
              maxLength={500}
              rows={3}
            />
          </FieldGroup>
        </>
      )}
    </div>
  );
}

function AgentPicker({
  agents,
  loading,
  onSelect,
}: {
  agents: AgentOption[];
  loading: boolean;
  onSelect: (agent: AgentOption) => void;
}) {
  return (
    <Command className="rounded-md border shadow-sm" filter={(value, search) => {
      // `value` is the lowercased cmdk value we set (email). We also want to
      // match by name, so compose a searchable haystack instead.
      const haystack = value.toLowerCase();
      return haystack.includes(search.toLowerCase()) ? 1 : 0;
    }}>
      <CommandInput placeholder="Search by name or email…" />
      <CommandList className="max-h-[240px]">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading agents…
          </div>
        ) : agents.length === 0 ? (
          <CommandEmpty>No agents with saved cards found.</CommandEmpty>
        ) : (
          <>
            <CommandEmpty>No matches.</CommandEmpty>
            <CommandGroup>
              {agents.map((agent) => {
                const searchableValue = `${agent.name} ${agent.email}`.toLowerCase();
                return (
                  <CommandItem
                    key={agent.email}
                    value={searchableValue}
                    onSelect={() => onSelect(agent)}
                    className="flex items-center justify-between gap-3 py-2 cursor-pointer"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{agent.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{agent.email}</div>
                    </div>
                    {agent.activeContracts > 0 ? (
                      <Badge variant="outline" className="flex-shrink-0 bg-green-500/10 text-green-700 border-green-200 text-[10px]">
                        {agent.activeContracts} active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="flex-shrink-0 text-[10px] text-muted-foreground">
                        inactive
                      </Badge>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  );
}

function SelectedAgentCard({
  agent,
  onChange,
}: {
  agent: AgentOption;
  onChange: () => void;
}) {
  return (
    <div className="rounded-md border bg-muted/20 p-4 flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0">
        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <UserCheck className="h-4 w-4" />
        </div>
        <div className="min-w-0 space-y-0.5">
          <div className="text-sm font-semibold truncate">{agent.name}</div>
          <div className="text-xs text-muted-foreground truncate">{agent.email}</div>
          <div className="flex items-center gap-2 pt-1">
            <Badge variant="outline" className="text-[10px] font-mono">
              {agent.stripeCustomerId}
            </Badge>
            {agent.activeContracts > 0 && (
              <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-200 text-[10px]">
                {agent.activeContracts} active contract{agent.activeContracts === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onChange} className="flex-shrink-0">
        <Pencil className="h-3 w-3 mr-1" />
        Change
      </Button>
    </div>
  );
}

function SectionHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="space-y-1">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface ReviewPanelProps {
  selectedAgent: AgentOption;
  vertical: string;
  leadTypes: string[];
  totalLeads: number;
  pricePerLead: number;
  cycleDays: number;
  totalLabel: string;
  authorized: boolean;
  setAuthorized: (v: boolean) => void;
  confirmText: string;
  setConfirmText: (v: string) => void;
}

function ReviewPanel(props: ReviewPanelProps) {
  return (
    <div className="space-y-5">
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          Clicking charge will <strong>immediately debit</strong> the agent's saved card.
          This action cannot be undone without issuing a refund.
        </div>
      </div>

      <div className="rounded-md border bg-muted/20 divide-y text-sm">
        <Row label="Agent" value={`${props.selectedAgent.name} (${props.selectedAgent.email})`} />
        <Row
          label="Stripe customer"
          value={<span className="font-mono text-xs">{props.selectedAgent.stripeCustomerId}</span>}
        />
        <Row label="Vertical" value={props.vertical === "life_insurance" ? "Life Insurance" : "Mortgage"} />
        <Row
          label="Lead types"
          value={
            <div className="flex flex-wrap gap-1 justify-end">
              {props.leadTypes.map((t) => (
                <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
              ))}
            </div>
          }
        />
        <Row label="Leads" value={`${props.totalLeads}`} />
        <Row label="Price / lead" value={`$${props.pricePerLead.toFixed(2)}`} />
        <Row label="Cycle duration" value={`${props.cycleDays} days`} />
        <Row
          label="Total charge"
          value={<span className="text-base font-semibold tabular-nums">{props.totalLabel}</span>}
        />
      </div>

      <div className="space-y-4 rounded-md border border-amber-200 bg-amber-50/50 p-4">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={props.authorized}
            onChange={(e) => props.setAuthorized(e.target.checked)}
          />
          <span className="text-sm text-amber-900">
            I have confirmed with the agent that they authorize this specific charge.
          </span>
        </label>

        <div className="space-y-1.5">
          <Label className="text-xs text-amber-900">
            Type <span className="font-mono font-semibold">CHARGE</span> to enable the button
          </Label>
          <Input
            value={props.confirmText}
            onChange={(e) => props.setConfirmText(e.target.value)}
            placeholder="CHARGE"
            className="font-mono bg-white"
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ResultPanel({
  result,
  totalLabel,
  onCopy,
}: {
  result: ChargeResult;
  totalLabel: string;
  onCopy: (text: string) => void;
}) {
  if (result.status === "paid") {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-green-300 bg-green-50 p-4 flex items-start gap-2 text-sm">
          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-green-800">Charge successful</p>
            <p className="text-green-700 mt-1">
              {totalLabel} has been charged to the agent's saved card. The contract will appear
              in the dashboard within a few seconds (created by the Stripe webhook).
            </p>
          </div>
        </div>
        <ReferenceRow label="Invoice ID" value={result.invoiceId} onCopy={onCopy} />
        <ReferenceRow label="Proposal ID" value={result.proposalId} onCopy={onCopy} />
      </div>
    );
  }

  if (result.status === "pending") {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-blue-300 bg-blue-50 p-4 flex items-start gap-2 text-sm">
          <Loader2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5 animate-spin" />
          <div>
            <p className="font-medium text-blue-800">Charge pending</p>
            <p className="text-blue-700 mt-1">
              Stripe accepted the charge but hasn't confirmed final status yet. The contract will
              be created automatically once the payment settles — watch the Payment Links table
              for the contract_created status.
            </p>
          </div>
        </div>
        <ReferenceRow label="Invoice ID" value={result.invoiceId} onCopy={onCopy} />
        <ReferenceRow label="Proposal ID" value={result.proposalId} onCopy={onCopy} />
      </div>
    );
  }

  if (result.status === "auth_required") {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 flex items-start gap-2 text-sm">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Customer authentication required</p>
            <p className="text-amber-700 mt-1">
              The card issuer requested 3D Secure authentication. The invoice is open — share the
              hosted URL below with the agent so they can complete the authentication. Once they
              do, the contract will be created automatically.
            </p>
          </div>
        </div>
        {result.hostedInvoiceUrl && (
          <ReferenceRow label="Hosted invoice URL" value={result.hostedInvoiceUrl} onCopy={onCopy} />
        )}
        <ReferenceRow label="Invoice ID" value={result.invoiceId} onCopy={onCopy} />
        <ReferenceRow label="Proposal ID" value={result.proposalId} onCopy={onCopy} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 flex items-start gap-2 text-sm">
        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-destructive">Charge failed</p>
          <p className="text-destructive/90 mt-1">{result.error}</p>
          <p className="text-destructive/80 mt-2 text-xs">
            The invoice has been voided — no funds were captured. Try again or send the agent
            a standard Stripe Checkout link instead.
          </p>
        </div>
      </div>
      {result.proposalId && <ReferenceRow label="Proposal ID" value={result.proposalId} onCopy={onCopy} />}
    </div>
  );
}

function ReferenceRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (text: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground w-32 flex-shrink-0">{label}</span>
      <span className="font-mono flex-1 truncate">{value}</span>
      <Button variant="ghost" size="sm" onClick={() => onCopy(value)} className="h-7 px-2">
        <Copy className="h-3 w-3" />
      </Button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
